// src/pages/ReleaseNotesViewer.js
import React from 'react';
import { sanitizeHtml } from '../utils/sanitize';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Alert
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { releaseNotesContent } from '../data/releaseNotesContent';

const ReleaseNotesViewer = () => {
  const { version } = useParams();
  const navigate = useNavigate();

  const content = releaseNotesContent[version];

  const handleBack = () => {
    navigate('/help');
  };

  if (!content) {
    return (
      <Box sx={{ p: 3 }}>
        <IconButton onClick={handleBack} sx={{ mb: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Alert severity="error">Release notes for {version} not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={handleBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">Release Notes</Typography>
      </Box>

      <Paper
        sx={{
          p: 0,
          overflow: 'hidden'
        }}
      >
        {/* Include Font Awesome for icons */}
        <link
          rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"
        />
        <style>{`
          .container { max-width: 100% !important; margin: 0 !important; border-radius: 0 !important; box-shadow: none !important; }
          .header { background: linear-gradient(135deg, #9A3F69 0%, #B15483 100%); color: white; padding: 40px 30px; text-align: center; }
          .header h1 { margin: 0; font-size: 28px; font-weight: 600; display: flex; align-items: center; justify-content: center; gap: 15px; flex-wrap: wrap; }
          .header p { margin: 15px 0 0 0; font-size: 18px; opacity: 0.95; }
          .version-badge { display: inline-block; background: rgba(255,255,255,0.2); padding: 12px 24px; border-radius: 20px; margin-top: 15px; font-size: 14px; font-weight: 600; }
          .content { padding: 30px; }
          .content h2 { color: #9A3F69; font-size: 24px; margin-top: 30px; margin-bottom: 15px; border-bottom: 3px solid #F4E6ED; padding-bottom: 10px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
          .content h3 { color: #B15483; font-size: 20px; margin-top: 20px; margin-bottom: 12px; }
          .content p { margin: 12px 0; color: #555555; line-height: 1.6; }
          .highlight-box { background-color: #FFF5E6; border-left: 4px solid #DAA03D; padding: 16px 20px; margin: 20px 0; border-radius: 6px; }
          .highlight-box.new { background-color: #e3f2fd; border-left-color: #2196f3; }
          .highlight-box.success { background-color: #e8f5e8; border-left-color: #4caf50; }
          .highlight-box.info { background-color: #F4E6ED; border-left-color: #9A3F69; }
          .highlight-box strong { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; font-size: 16px; color: #9A3F69; flex-wrap: wrap; }
          .feature-list { list-style: none; padding: 0; margin: 16px 0; }
          .feature-list li { padding: 10px 0 10px 35px; position: relative; color: #555555; margin-bottom: 8px; line-height: 1.5; }
          .feature-list li:before { content: "✓"; position: absolute; left: 8px; top: 10px; color: #4caf50; font-weight: bold; }
          .feature-list li.filter:before { content: "🔍"; }
          .feature-list li.building:before { content: "🏢"; }
          .feature-list li.check:before { content: "✓"; color: #4caf50; }
          .feature-list li.star:before { content: "⭐"; }
          .feature-list li.wrench:before { content: "🔧"; }
          .gif-container { text-align: center; margin: 25px 0; padding: 20px; background-color: #f9f9f9; border-radius: 8px; border: 1px solid #e0e0e0; }
          .gif-container img { max-width: 100%; height: auto; border-radius: 6px; box-shadow: 0 2px 12px rgba(0,0,0,0.15); }
          .gif-container p { margin-top: 12px; font-size: 14px; color: #888; font-style: italic; }
          .footer { background-color: #f5f5f5; padding: 20px; text-align: center; color: #555555; font-size: 14px; border-top: 1px solid #e0e0e0; }
        `}</style>
        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />
      </Paper>
    </Box>
  );
};

export default ReleaseNotesViewer;
